// src/pages/superadmin/SuperAdminAirports.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Building2, Plus, Edit2, Trash2, Search, X, CheckCircle, XCircle,
  Key, ChevronLeft, ChevronRight, Cpu, RefreshCw, CreditCard,
  Percent, AlertTriangle, Loader, Unlink, Plane
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ── Modal Stripe par aéroport ─────────────────────────────────────────────────
const StripeModal = ({ airport, onClose }) => {
  const [stripeData, setStripeData]   = useState(null);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [fee, setFee]                 = useState('');
  const [feedback, setFeedback]       = useState(null);

  useEffect(() => { fetchStripeData(); }, []);

  const fetchStripeData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/admin/airports/${airport.id}/stripe`);
      setStripeData(res.data);
      setFee(res.data.aerotrick_fee_percent?.toString() || '1.50');
    } catch (e) {
      setFeedback({ type: 'error', message: 'Error loading Stripe data' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFee = async () => {
    const parsed = parseFloat(fee);
    if (isNaN(parsed) || parsed < 0 || parsed > 20) {
      setFeedback({ type: 'error', message: 'Fee must be between 0% and 20%' });
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/admin/airports/${airport.id}/stripe/fee`, { aerotrick_fee_percent: parsed });
      setFeedback({ type: 'success', message: `Fee updated to ${parsed}%` });
      await fetchStripeData();
    } catch (e) {
      setFeedback({ type: 'error', message: e.response?.data?.detail || 'Error saving fee' });
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(`Disconnect Stripe account for ${airport.name}?`)) return;
    setDisconnecting(true);
    try {
      await axios.delete(`${API_BASE}/admin/airports/${airport.id}/stripe/disconnect`);
      setFeedback({ type: 'success', message: 'Stripe account disconnected' });
      await fetchStripeData();
    } catch (e) {
      setFeedback({ type: 'error', message: e.response?.data?.detail || 'Error disconnecting' });
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-indigo-400" />
            <h3 className="text-white font-semibold">Stripe Settings</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-4">
          <div className="bg-gray-900/60 rounded-lg p-3">
            <p className="text-white font-medium">{airport.name}</p>
            <p className="text-gray-500 text-xs">{airport.subdomain}.aerotrick.com</p>
          </div>
          {feedback && (
            <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${
              feedback.type === 'success'
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : 'bg-red-500/20 text-red-400 border border-red-500/30'
            }`}>
              {feedback.type === 'success' ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 flex-shrink-0" />}
              {feedback.message}
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center py-6 gap-2 text-gray-400">
              <Loader className="w-5 h-5 animate-spin" />Loading...
            </div>
          ) : (
            <>
              <div className="bg-gray-900/60 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Stripe Connect</span>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                    stripeData?.stripe_onboarded
                      ? 'bg-green-500/20 text-green-400 border-green-500/30'
                      : 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full bg-current ${stripeData?.stripe_onboarded ? 'animate-pulse' : ''}`} />
                    {stripeData?.stripe_onboarded ? 'Connected' : 'Not connected'}
                  </span>
                </div>
                {stripeData?.stripe_account_preview && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Account</span>
                    <span className="text-white font-mono">{stripeData.stripe_account_preview}</span>
                  </div>
                )}
                {stripeData?.stripe_onboarded_at && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Connected on</span>
                    <span className="text-gray-300">{new Date(stripeData.stripe_onboarded_at).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <label className="block text-sm text-gray-400">AeroTrick Platform Fee (%)</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input type="number" min="0" max="20" step="0.1" value={fee}
                      onChange={(e) => setFee(e.target.value)}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-white pr-8 focus:outline-none focus:border-indigo-500" />
                    <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  </div>
                  <button onClick={handleSaveFee} disabled={saving}
                    className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 rounded-lg text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2 transition">
                    {saving ? <Loader className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
                <p className="text-xs text-gray-500">Applied automatically on each fuel transaction. Default: 1.50%</p>
              </div>
              {fee && !isNaN(parseFloat(fee)) && (
                <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-lg p-3 text-sm">
                  <p className="text-gray-400 mb-1">Example on a $350 transaction:</p>
                  <div className="flex justify-between text-white">
                    <span>AeroTrick fee ({fee}%)</span>
                    <span className="font-semibold text-indigo-400">${(350 * parseFloat(fee) / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-300 mt-1">
                    <span>Airport receives</span>
                    <span>${(350 - (350 * parseFloat(fee) / 100)).toFixed(2)}</span>
                  </div>
                </div>
              )}
              {stripeData?.stripe_onboarded && (
                <button onClick={handleDisconnect} disabled={disconnecting}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm transition disabled:opacity-50">
                  {disconnecting ? <Loader className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                  {disconnecting ? 'Disconnecting...' : 'Force Disconnect Stripe'}
                </button>
              )}
            </>
          )}
        </div>
        <div className="p-4 border-t border-gray-700">
          <button onClick={onClose} className="w-full py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm transition">Close</button>
        </div>
      </div>
    </div>
  );
};

// ── Modal Movement Fee par aéroport ───────────────────────────────────────────
const MovementFeeModal = ({ airport, onClose }) => {
  const [fee,      setFee]      = useState('15.00');
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => { fetchCurrentFee(); }, []);

  const fetchCurrentFee = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/admin/airports/${airport.id}/movement-fee`);
      setFee(res.data.aerotrick_movement_fee_percent?.toString() || '15.00');
    } catch (e) {
      setFeedback({ type: 'error', message: 'Error loading movement fee' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const parsed = parseFloat(fee);
    if (isNaN(parsed) || parsed < 0 || parsed > 50) {
      setFeedback({ type: 'error', message: 'Fee must be between 0% and 50%' });
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_BASE}/admin/airports/${airport.id}/movement-fee`, {
        aerotrick_movement_fee_percent: parsed,
      });
      setFeedback({ type: 'success', message: `Movement fee updated to ${parsed}%` });
    } catch (e) {
      setFeedback({ type: 'error', message: e.response?.data?.detail || 'Error saving fee' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-2">
            <Plane className="w-5 h-5 text-sky-400" />
            <h3 className="text-white font-semibold">Movement Fee Settings</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-4">

          {/* Airport info */}
          <div className="bg-gray-900/60 rounded-lg p-3">
            <p className="text-white font-medium">{airport.name}</p>
            <p className="text-gray-500 text-xs">{airport.subdomain}.aerotrick.com</p>
          </div>

          {/* Feedback */}
          {feedback && (
            <div className={`p-3 rounded-lg flex items-center gap-2 text-sm ${
              feedback.type === 'success'
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : 'bg-red-500/20 text-red-400 border border-red-500/30'
            }`}>
              {feedback.type === 'success'
                ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
                : <AlertTriangle className="w-4 h-4 flex-shrink-0" />}
              {feedback.message}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-6 gap-2 text-gray-400">
              <Loader className="w-5 h-5 animate-spin" />Loading...
            </div>
          ) : (
            <>
              {/* Info box */}
              <div className="bg-sky-500/10 border border-sky-500/20 rounded-lg p-3 text-sm">
                <p className="text-sky-400 font-medium flex items-center gap-1.5 mb-1">
                  <Plane className="w-3.5 h-3.5" />
                  Takeoff & Landing Commission
                </p>
                <p className="text-gray-400 text-xs">
                  This percentage is deducted from each landing and takeoff fee collected by the airport and paid to Aerotrick automatically via Stripe Connect.
                </p>
              </div>

              {/* Fee input */}
              <div className="space-y-2">
                <label className="block text-sm text-gray-400">
                  Aerotrick Movement Fee (%)
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type="number"
                      min="0"
                      max="50"
                      step="0.5"
                      value={fee}
                      onChange={(e) => setFee(e.target.value)}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 text-white pr-8 focus:outline-none focus:border-sky-500"
                    />
                    <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  </div>
                  <button onClick={handleSave} disabled={saving}
                    className="px-4 py-2 bg-sky-500 hover:bg-sky-600 rounded-lg text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2 transition">
                    {saving ? <Loader className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  Applied on each movement invoice. Default: 15%
                </p>
              </div>

              {/* Preview */}
              {fee && !isNaN(parseFloat(fee)) && (
                <div className="bg-sky-500/10 border border-sky-500/20 rounded-lg p-3 text-sm space-y-2">
                  <p className="text-gray-400 text-xs font-medium">Example calculations:</p>
                  {[
                    { label: 'Light aircraft landing ($20)', base: 20 },
                    { label: 'Medium aircraft landing ($75)', base: 75 },
                    { label: 'Heavy aircraft landing ($200)', base: 200 },
                  ].map(({ label, base }) => (
                    <div key={label}>
                      <p className="text-gray-500 text-xs">{label}</p>
                      <div className="flex justify-between text-xs mt-0.5">
                        <span className="text-sky-400">Aerotrick ({fee}%)</span>
                        <span className="font-semibold text-sky-400">
                          ${(base * parseFloat(fee) / 100).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400">Airport receives</span>
                        <span className="text-gray-300">
                          ${(base - (base * parseFloat(fee) / 100)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-4 border-t border-gray-700">
          <button onClick={onClose}
            className="w-full py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm transition">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const SuperAdminAirports = () => {
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingAirport, setEditingAirport] = useState(null);
  const [showAgentsModal, setShowAgentsModal] = useState(false);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [showMovementFeeModal, setShowMovementFeeModal] = useState(false); // ← NEW
  const [selectedAirport, setSelectedAirport] = useState(null);
  const [airportLicense, setAirportLicense] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [updatingAgents, setUpdatingAgents] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '', subdomain: '', contact_email: '', contact_phone: '',
    address: '', timezone: 'America/Chicago',
    max_staff: 50, max_owners: 500, max_agents: 5, expires_at: ''
  });

  useEffect(() => { loadAirports(); }, [page, searchTerm]);

  const loadAirports = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/admin/airports/`, {
        params: { page, limit: 20, search: searchTerm }
      });
      setAirports(response.data.airports || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error loading airports:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAirportLicense = async (airportId) => {
    try {
      const response = await axios.get(`${API_BASE}/admin/airports/${airportId}/license`);
      setAirportLicense(response.data);
    } catch (error) { setAirportLicense(null); }
  };

  const handleOpenAgentsModal = async (airport) => {
    setSelectedAirport(airport);
    await loadAirportLicense(airport.id);
    setShowAgentsModal(true);
  };

  const handleOpenStripeModal = (airport) => {
    setSelectedAirport(airport);
    setShowStripeModal(true);
  };

  const handleOpenMovementFeeModal = (airport) => { // ← NEW
    setSelectedAirport(airport);
    setShowMovementFeeModal(true);
  };

  const handleUpdateMaxAgents = async () => {
    if (!selectedAirport || !airportLicense) return;
    setUpdatingAgents(true);
    try {
      await axios.put(`/admin/airports/${selectedAirport.id}/license/agents`, {
        max_agents: airportLicense.max_agents
      });
      await loadAirports();
      await loadAirportLicense(selectedAirport.id);
      alert(`✅ Max agents updated to ${airportLicense.max_agents}`);
    } catch (error) {
      alert('❌ Error updating max agents');
    } finally {
      setUpdatingAgents(false);
    }
  };

  const handleEdit = (airport) => {
    setEditingAirport(airport);
    setFormData({
      name: airport.name || '',
      subdomain: airport.subdomain || '',
      contact_email: airport.contact_email || '',
      contact_phone: airport.contact_phone || '',
      address: airport.address || '',
      timezone: airport.timezone || 'America/Chicago',
      max_staff: 50, max_owners: 500,
      max_agents: airport.max_agents || 5,
      expires_at: ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingAirport(null);
    setFormData({
      name: '', subdomain: '', contact_email: '', contact_phone: '',
      address: '', timezone: 'America/Chicago',
      max_staff: 50, max_owners: 500, max_agents: 5, expires_at: ''
    });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.subdomain) {
      alert('Name and subdomain are required');
      return;
    }
    setSubmitting(true);
    try {
      if (editingAirport) {
        await axios.put(`${API_BASE}/admin/airports/${editingAirport.id}`, formData);
      } else {
        const response = await axios.post(`${API_BASE}/admin/airports/`, formData);
        if (response.data.license_key) {
          alert(`✅ Airport created!\nLicense Key: ${response.data.license_key}\n\nSave this key for the client.`);
        }
      }
      setShowModal(false);
      resetForm();
      await loadAirports();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (airport) => {
    if (!window.confirm(`Delete ${airport.name}? This will also delete its database.`)) return;
    try {
      await axios.delete(`${API_BASE}/admin/airports/${airport.id}`);
      await loadAirports();
    } catch (error) {
      alert('Error deleting airport: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleResetPassword = async (airport) => {
    if (!window.confirm(`Reset admin password for ${airport.name}?`)) return;
    try {
      const response = await axios.post(`${API_BASE}/admin/airports/${airport.id}/reset-password`);
      setResetResult({ airport: airport.name, password: response.data.new_password });
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Building2 className="w-6 h-6 text-blue-400" />
            Airports Management
          </h1>
          <p className="text-gray-400 text-sm">Manage client airports, agent licenses, Stripe fees and movement commissions</p>
        </div>
        <button onClick={() => { resetForm(); setShowModal(true); }}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white flex items-center gap-2">
          <Plus className="w-4 h-4" />Add Airport
        </button>
      </div>

      {/* Reset Password Result */}
      {resetResult && (
        <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4">
          <p className="text-green-400 font-medium">Password reset for {resetResult.airport}</p>
          <p className="text-white text-lg font-mono mt-1">{resetResult.password}</p>
          <p className="text-gray-400 text-xs mt-1">Give this password to the airport administrator.</p>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input type="text" placeholder="Search airports..."
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-10 pr-4 text-white" />
      </div>

      {/* Table */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800 border-b border-gray-700">
              <tr className="text-gray-400 text-sm">
                <th className="p-4 text-left">Airport</th>
                <th className="p-4 text-left">Subdomain</th>
                <th className="p-4 text-left">Contact</th>
                <th className="p-4 text-left">Agents</th>
                <th className="p-4 text-left">Stripe</th>
                <th className="p-4 text-left">Mov. Fee</th>
                <th className="p-4 text-left">Status</th>
                <th className="p-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" className="text-center py-12 text-gray-500">
                  <Loader className="w-6 h-6 animate-spin mx-auto mb-2" />Loading...
                </td></tr>
              ) : airports.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-12 text-gray-500">No airports found</td></tr>
              ) : airports.map((airport) => (
                <tr key={airport.id} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                  <td className="p-4">
                    <div>
                      <p className="text-white font-medium">{airport.name}</p>
                      <p className="text-gray-500 text-xs">ID: {airport.id.substring(0, 8)}...</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <code className="text-blue-400 text-sm">{airport.subdomain}.aerotrick.com</code>
                  </td>
                  <td className="p-4">
                    <p className="text-gray-300 text-sm">{airport.contact_email || '-'}</p>
                    <p className="text-gray-500 text-xs">{airport.contact_phone || '-'}</p>
                  </td>
                  <td className="p-4">
                    <span className="px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400">
                      {airport.used_agents || 0}/{airport.max_agents || 5}
                    </span>
                  </td>

                  {/* Stripe status */}
                  <td className="p-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      airport.stripe_onboarded
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-gray-500/20 text-gray-500'
                    }`}>
                      <CreditCard className="w-3 h-3" />
                      {airport.stripe_onboarded ? 'Connected' : 'Not set'}
                    </span>
                  </td>

                  {/* Movement fee */}
                  <td className="p-4">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-sky-500/20 text-sky-400">
                      <Plane className="w-3 h-3" />
                      {airport.aerotrick_movement_fee_percent
                        ? `${parseFloat(airport.aerotrick_movement_fee_percent).toFixed(1)}%`
                        : '15.0%'}
                    </span>
                  </td>

                  <td className="p-4">
                    {airport.is_active
                      ? <span className="flex items-center gap-1 text-green-400 text-xs"><CheckCircle className="w-3 h-3" /> Active</span>
                      : <span className="flex items-center gap-1 text-red-400 text-xs"><XCircle className="w-3 h-3" /> Inactive</span>}
                  </td>

                  <td className="p-4">
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(airport)}
                        className="p-1.5 hover:bg-gray-700 rounded-lg text-blue-400" title="Edit">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleOpenAgentsModal(airport)}
                        className="p-1.5 hover:bg-gray-700 rounded-lg text-purple-400" title="Manage Agents">
                        <Cpu className="w-4 h-4" />
                      </button>
                      {/* Stripe */}
                      <button onClick={() => handleOpenStripeModal(airport)}
                        className="p-1.5 hover:bg-gray-700 rounded-lg text-indigo-400" title="Stripe Settings">
                        <CreditCard className="w-4 h-4" />
                      </button>
                      {/* Movement Fee ← NEW */}
                      <button onClick={() => handleOpenMovementFeeModal(airport)}
                        className="p-1.5 hover:bg-gray-700 rounded-lg text-sky-400" title="Movement Fee Settings">
                        <Plane className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleResetPassword(airport)}
                        className="p-1.5 hover:bg-gray-700 rounded-lg text-yellow-400" title="Reset Password">
                        <Key className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(airport)}
                        className="p-1.5 hover:bg-gray-700 rounded-lg text-red-400" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 p-4 border-t border-gray-700">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 disabled:opacity-50">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-gray-400 text-sm py-2">Page {page} of {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 disabled:opacity-50">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Stripe Modal */}
      {showStripeModal && selectedAirport && (
        <StripeModal
          airport={selectedAirport}
          onClose={() => { setShowStripeModal(false); setSelectedAirport(null); }}
        />
      )}

      {/* Movement Fee Modal ← NEW */}
      {showMovementFeeModal && selectedAirport && (
        <MovementFeeModal
          airport={selectedAirport}
          onClose={() => { setShowMovementFeeModal(false); setSelectedAirport(null); loadAirports(); }}
        />
      )}

      {/* Agents Modal */}
      {showAgentsModal && selectedAirport && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-2xl border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold">Agents for {selectedAirport.name}</h3>
              <button onClick={() => setShowAgentsModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-gray-900 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400">License Key:</span>
                  <code className="text-blue-400 text-sm">{airportLicense?.license_key}</code>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400">Agents Used:</span>
                  <span className="text-white font-medium">{airportLicense?.used_agents || 0} / {airportLicense?.max_agents || 5}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Max Agents:</span>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" max="100" value={airportLicense?.max_agents || 5}
                      onChange={(e) => setAirportLicense(prev => ({ ...prev, max_agents: parseInt(e.target.value) }))}
                      className="w-20 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-white text-sm" />
                    <button onClick={handleUpdateMaxAgents} disabled={updatingAgents}
                      className="px-3 py-1 bg-purple-500 hover:bg-purple-600 rounded text-white text-sm">
                      {updatingAgents ? 'Updating...' : 'Update'}
                    </button>
                  </div>
                </div>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-blue-400 text-sm font-medium mb-2">📋 License Key</p>
                <code className="block bg-gray-900 p-2 rounded mt-2 text-blue-400 text-sm break-all">
                  {airportLicense?.license_key}
                </code>
              </div>
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button onClick={() => setShowAgentsModal(false)}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl w-full max-w-2xl border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold">{editingAirport ? 'Edit Airport' : 'New Airport'}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Airport Name *</label>
                  <input type="text" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Subdomain *</label>
                  <input type="text" value={formData.subdomain}
                    onChange={(e) => setFormData({ ...formData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                  <p className="text-xs text-gray-500 mt-1">.aerotrick.com</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Contact Email</label>
                  <input type="email" value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Contact Phone</label>
                  <input type="tel" value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Address</label>
                <textarea value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows="2" className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Max Staff</label>
                  <input type="number" value={formData.max_staff}
                    onChange={(e) => setFormData({ ...formData, max_staff: parseInt(e.target.value) })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Max Owners</label>
                  <input type="number" value={formData.max_owners}
                    onChange={(e) => setFormData({ ...formData, max_owners: parseInt(e.target.value) })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Max Agents</label>
                  <input type="number" value={formData.max_agents}
                    onChange={(e) => setFormData({ ...formData, max_agents: parseInt(e.target.value) })}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" />
                </div>
              </div>
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button onClick={handleSubmit} disabled={submitting}
                className="flex-1 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white font-medium disabled:opacity-50">
                {submitting ? 'Saving...' : (editingAirport ? 'Update' : 'Create')}
              </button>
              <button onClick={() => setShowModal(false)}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminAirports;